// import { yupToFormErrors } from "formik";
import * as Yup from "yup";

import { checkAliasAvailable } from "../http/events";
import { eventTypes, roles } from "@/components/globalUtils";
import { t } from "@/localization";

export const validateAlias = async (value) => {
  if (value) {
    return await checkAliasAvailable(value);
  } else {
    return true;
  }
};

export const SignInSchema = Yup.object().shape({
  email: Yup.string().email(t("Invalid email")).required(t("Required")),
  password: Yup.string().min(6, t("Too Short!")).required(t("Required")),
});

export const SignUpSchema = Yup.object().shape({
  name: Yup.string()
    .min(1, t("Too Short!"))
    .max(50, t("Too Long!"))
    .required(t("Please enter your full name")),
  // firstname: Yup.string()
  //   .min(1, "Too Short!")
  //   .max(50, "Too Long!")
  //   .required("Required"),
  // lastname: Yup.string()
  //   .min(1, "Too Short!")
  //   .max(50, "Too Long!")
  //   .required("Required"),
  email: Yup.string()
    .email(t("Invalid email"))
    .required(t("Please enter a valid email")),
  password: Yup.string()
    .min(6, t("Password is too short"))
    .required(t("Password is required")),
});

export const SegmentationSchema = Yup.object().shape({
  account_type: Yup.string().oneOf(["individual", "business"]),
  broadcaster_role: Yup.string().oneOf([
    "videographer",
    "photographer",
    "funeralhome",
    "eventplanner",
    "other",
  ]),
  role_other: Yup.string(),
  company_name: Yup.string(),
  company_url: Yup.string(),
});

// Profile (auto-saved). The email has its own form: ChangeEmailSchema.
export const ProfileSchema = Yup.object().shape({
  first_name: Yup.string()
    .trim()
    .max(50, t("50 characters at most"))
    .required(t("Enter your first name")),
  last_name: Yup.string().max(50, t("50 characters at most")),
  account_type: Yup.string().oneOf(["individual", "business"]),
  // Every role the onboarding offers (nonprofit, church, school, …) plus the
  // retired "photographer"; otherwise profile autosave silently stops.
  broadcaster_role: Yup.string()
    .oneOf([...roles.map((r) => r.value), "photographer", null])
    .nullable(),
  role_other: Yup.string().nullable(),
  company_name: Yup.string().nullable(),
  company_url: Yup.string().nullable(),
  no_branding: Yup.bool().nullable(),
});

export const ChangeEmailSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .email(t("Enter an email address like name@example.com"))
    .required(t("Enter your email")),
});

export const EmailSchema = Yup.object().shape({
  email: Yup.string().email(t("Invalid email")).required(t("Required")),
});

export const NewPasswordSchema = Yup.object().shape({
  password: Yup.string().min(6, t("Too Short!")).required(t("Required")),
});

export const EventShape = {
  title: Yup.string().required(t("Title is Required")),

  event_location: Yup.string(),
  event_type: Yup.string().oneOf(eventTypes.map((r) => r.value)),
  guest_message: Yup.string(),
};

export const EventDateTimeDaysLinkShape = {
  event_datetime: Yup.date().required(t("Required")),
  event_timezone: Yup.string().required(t("Timezone needs to be detected")),
  days: Yup.number()
    .typeError(t("Please enter number of days"))
    .integer(t("Please enter number of days"))
    .min(1, t("At least 1 day"))
    .max(7, t("7 days max")),
  // event_date: Yup.string().required("Date is Required"),
  // event_time: Yup.string().required("Time is Required"),
  alias: Yup.string()
    .required(t("Please choose your custom event link"))
    .nullable()
    .test("checkAvailability", t("Sorry, Already Taken"), validateAlias)
    .matches(
      /^[\d\w-_]+$/,
      t("Only letters, numbers, dashes and underscore allowed.")
    ),
};

export const CreateEventSchema = Yup.object().shape({
  ...EventShape,
  ...EventDateTimeDaysLinkShape,
  title: Yup.string().trim().required(t("Add a title")),
  event_datetime: Yup.date().nullable().required(t("Choose the date and time")),
  alias: Yup.string()
    .required(t("Choose a link for your event"))
    .nullable()
    .matches(
      /^[\d\w-_]+$/,
      t("Use only letters, numbers, dashes and underscores.")
    )
    .test(
      "checkAvailability",
      t("That link is taken. Try another."),
      validateAlias
    ),
});

export const LinkSchema = Yup.object().shape({
  url: Yup.string().required(t("Link URL is required")),
  title: Yup.string().required(t("Title is required")),
  file_upload: Yup.bool().nullable(),
});

export const EventSchema = Yup.object().shape({
  ...EventShape,
  links: Yup.array().of(LinkSchema),
});

// Event settings › details. The links editor only adds complete links, and an
// old incomplete one (the event page skips those) must not stop the title and
// location from saving, so links aren't validated here.
export const EventDetailsSchema = Yup.object().shape({
  ...EventShape,
  title: Yup.string().trim().required(t("Add a title")),
  links: Yup.array(),
});

export const StripeSchema = Yup.object().shape({
  stripe_price_id: Yup.string()
    // .required("Provide Stripe Price Id")
    .matches(
      /^price_[a-zA-Z0-9]+$/,
      t(
        "Invalid format. It should be in the format of price_(letters_and_digits)"
      )
    )
    .nullable(),
  stripe_price_id_test: Yup.string()
    .matches(
      /^price_[a-zA-Z0-9]+$/,
      t(
        "Invalid format. It should be in the format of price_(letters_and_digits)"
      )
    )
    .nullable(),
});

export const EventDateTimeSchema = Yup.object().shape({
  event_datetime: Yup.date().required(t("Required")),
  event_timezone: Yup.string().required(t("Timezone needs to be detected")),
  days: Yup.number()
    .typeError(t("Please enter number of days"))
    .integer(t("Please enter number of days"))
    .min(1, t("At least 1 day"))
    .max(7, t("7 days max")),
});

export const AliasSchema = Yup.object().shape({
  alias: Yup.string()
    .required(t("Required"))
    .nullable()
    .test("checkAvailability", t("Sorry, Already Taken"), async (value) => {
      if (value) {
        return await checkAliasAvailable(value);
      } else {
        return true;
      }
    })
    .matches(
      /^[\d\w-_]+$/,
      t("Only letters, numbers, dashes and underscore allowed.")
    ),
});

export const EventClipSchema = Yup.object().shape({
  clipFrom: Yup.string().matches(
    /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/,
    t("Please use HH:MM:SS format")
  ),
  clipTo: Yup.string().matches(
    /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/,
    t("Please use HH:MM:SS format")
  ),
});

// Older events have some of these unset (null). Yup rejects null unless the
// field is nullable, and a rejected form never saved: privacy changes on such
// events were silently dropped.
export const PrivacySchema = Yup.object().shape({
  event_privacy: Yup.string().nullable(),
  guestbook_enabled: Yup.bool().nullable(),
  event_password: Yup.string().nullable(),
  download_enabled: Yup.bool().nullable(),
  prefer_chat: Yup.bool().nullable(),
  email_pref: Yup.string().nullable(),
  hide_viewer_count: Yup.bool().nullable(),
});

// Event branding fields are all optional and come back null when never set.
export const BrandingSchema = Yup.object().shape({
  description: Yup.string().nullable(),
  logo_path: Yup.string().nullable(),
  company_name: Yup.string().nullable(),
  company_url: Yup.string().nullable(),
  custom_date_display: Yup.string().nullable(),
});

export const GuestInviteSchema = Yup.object().shape({
  name: Yup.string().required(t("Please add Guest's name")),
  email: Yup.string()
    .email(t("Not a valid Email"))
    .required(t("Add guest's email to invite")),
});

export const GuestbookMessageSchema = Yup.object().shape({
  message: Yup.string().required(t("Enter the message")),
  notifyGuests: Yup.bool(),
});
